import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    AgencySettingsInput,
    MessageOverageSettingsInput,
    OverageSettingsInput,
    UpdateInput,
} from './input/update-input'
import {
    deleteCompanyColumn,
    deleteCompanyUser,
    fetchCompanies,
    fetchCompany,
    fetchCompanyUsage,
    fetchCompanyUsers,
    reorderCompanyColumn,
    sortCompanyColumnCandidates,
    startCompanyTrial,
    storeCompanyColumn,
    updateCompany,
    updateCompanyAgencySettings,
    updateCompanyColumn,
    updateCompanyMessageOverageSettings,
    updateCompanyOverageSettings,
    updateCompanyUserRole,
} from './api'
import { useAuth } from '../auth'
import { onErrorNotifyAboutValidationError } from '../util'
import { notification } from 'antd'
import { useTranslation } from 'react-i18next'
import { CompanyRole } from './company-role'
import { CompanyQuery } from './company'
import { INDEX_COMPANY_USER } from '../user/permission'

export function useCompanies({
    query,
}: {
    query?: CompanyQuery
} = {}) {
    return useQuery({
        queryFn: () => fetchCompanies({ query }),
        queryKey: ['companies', query],
        placeholderData: keepPreviousData,
    })
}

export function useCompany(companyId: string) {
    return useQuery({
        queryFn: () => {
            return fetchCompany(companyId)
        },
        queryKey: ['companies', companyId],
    })
}

export function useCompanyUsage(companyId: string) {
    return useQuery({
        queryFn: () => fetchCompanyUsage(companyId),
        queryKey: ['companies', companyId, 'usage'],
        enabled: !!companyId,
    })
}

export function useUpdateCompany(companyId?: string) {
    const { activeCompany } = useAuth()
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: UpdateInput) => updateCompany(companyId!, data),
        onSuccess: (company) => {
            queryClient.invalidateQueries({ queryKey: ['companies', company.id] })
            queryClient.invalidateQueries({ queryKey: ['companies'] })

            if (company.id === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
    })
}

export function useUpdateCompanyMessageOverageSettings(companyId?: string) {
    const { activeCompany } = useAuth()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: (data: MessageOverageSettingsInput) => updateCompanyMessageOverageSettings(companyId!, data),
        onSuccess: (company) => {
            notification.success({
                message: t('Usage settings updated'),
            })
            queryClient.invalidateQueries({ queryKey: ['companies', company.id] })
            queryClient.invalidateQueries({ queryKey: ['companies', company.id, 'usage'] })

            if (company.id === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useUpdateCompanyOverageSettings(companyId?: string) {
    const { activeCompany } = useAuth()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: (data: OverageSettingsInput) => updateCompanyOverageSettings(companyId!, data),
        onSuccess: (company) => {
            notification.success({
                message: t('Usage settings updated'),
            })
            queryClient.invalidateQueries({ queryKey: ['companies', company.id] })
            queryClient.invalidateQueries({ queryKey: ['companies', company.id, 'usage'] })

            if (company.id === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useUpdateCompanyAgencySettings(companyId?: string) {
    const { activeCompany } = useAuth()
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: (data: AgencySettingsInput) => updateCompanyAgencySettings(companyId!, data),
        onSuccess: (company) => {
            notification.success({
                message: t('Agency settings updated'),
            })
            queryClient.invalidateQueries({ queryKey: ['companies'] })
            queryClient.invalidateQueries({ queryKey: ['companies', company.id] })
            queryClient.invalidateQueries({ queryKey: ['companies', company.id, 'usage'] })

            if (company.id === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useStartCompanyTrial(companyId?: string) {
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: () => startCompanyTrial(companyId!),
        onSuccess: () => {
            notification.success({
                message: t('Trial started successfully'),
            })
            queryClient.invalidateQueries({ queryKey: ['companies', companyId] })
            queryClient.invalidateQueries({ queryKey: ['me'] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useDeleteCompanyUser(companyId?: string, userId?: string) {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: () => deleteCompanyUser(companyId!, userId!),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', 'users', companyId] })
        },
    })
}

export function useUpdateCompanyUserRole(companyId?: string, userId?: string) {
    const queryClient = useQueryClient()
    const { t } = useTranslation()

    return useMutation({
        mutationFn: (role: CompanyRole) => updateCompanyUserRole(companyId!, userId!, role),
        onSuccess: () => {
            notification.success({
                message: t('Role updated successfully'),
            })
            queryClient.invalidateQueries({ queryKey: ['company', 'users', companyId] })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}

export function useCompanyUsers(companyId?: string) {
    const { can } = useAuth()

    return useQuery({
        queryFn: () => {
            return fetchCompanyUsers(companyId!)
        },
        queryKey: ['company', 'users', companyId],
        enabled: !!companyId && can(INDEX_COMPANY_USER),
    })
}

export function useReorderCompanyColumn(companyId: string) {
    const queryClient = useQueryClient()
    const { activeCompany } = useAuth()

    return useMutation({
        mutationFn: (order: string[]) => reorderCompanyColumn(companyId, order),
        onSuccess: () => {
            if (companyId === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
    })
}

export function useStoreCompanyColumn(companyId: string) {
    const queryClient = useQueryClient()
    const { activeCompany } = useAuth()

    return useMutation({
        mutationFn: (name: { name: string }) => storeCompanyColumn(companyId, name),
        onSuccess: () => {
            if (companyId === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
    })
}

export function useUpdateCompanyColumn(companyId: string, columnId: string) {
    const queryClient = useQueryClient()
    const { activeCompany } = useAuth()

    return useMutation({
        mutationFn: (name: string) => updateCompanyColumn(companyId, columnId, name),
        onSuccess: () => {
            if (companyId === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
    })
}

export function useDeleteCompanyColumn(companyId: string, columnId: string) {
    const queryClient = useQueryClient()
    const { activeCompany } = useAuth()

    return useMutation({
        mutationFn: () => deleteCompanyColumn(companyId, columnId),
        onSuccess: () => {
            if (companyId === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }
        },
    })
}

export function useSortCompanyColumnCandidates(companyId: string, columnId: string) {
    const queryClient = useQueryClient()
    const { activeCompany } = useAuth()

    return useMutation({
        mutationFn: sortCompanyColumnCandidates(companyId, columnId),
        onSuccess: () => {
            if (companyId === activeCompany?.id) {
                queryClient.invalidateQueries({ queryKey: ['me'] })
            }

            queryClient.invalidateQueries({
                queryKey: ['candidates', 'column', undefined, columnId],
                exact: false,
            })
        },
        onError: onErrorNotifyAboutValidationError,
    })
}
